/**
 * Phira's `max_time` (W1-05a engine-eval; rpe-format.md F§1, F§7.1, F§9, R§2.3, ARCHITECTURE.md §7.1).
 *
 *   max_time = max over every line (in export order) of
 *                - every note's endTime (holds or not; the IR carries a non-hold's original endTime while unedited),
 *                - the end of every event of alphaEvents / moveXEvents / moveYEvents / rotateEvents in EVERY
 *                  written (non-null) layer,
 *                - scaleXEvents / scaleYEvents,
 *                - textEvents on ANY line: the typed list and the written-back `ignoredLists` copy Phira still
 *                  parses on textured lines (raw JSON, parsed lazily here)
 *              starting from 0, plus 1 s.
 * Speed, colour, incline, paint and gif events do not count. After max_time both speed integrators freeze the
 * floor position, so this decides where heights stop.
 *
 * Internal API (W1-05b keeps one cached contribution per line for its dirty DAG):
 *   lineMaxTime(line, sec)          the line's contribution in seconds (-Infinity when it has nothing)
 *   combineMaxTime(contributions)   max(0, ...) + 1 s
 *   computeMaxTime(lines, sec)      both at once
 */

import type { IrEventList, IrLine } from '../ir/types';
import type { Sec, Triple } from '../time/types';
import type { Json } from '../util/types';
import { extendedOf, ignoredList } from './bezierRegistry';

/** Seconds added after the last item (`max_time + 1.0`). */
export const MAX_TIME_TAIL_SEC = 1;

/** A raw JSON beat triple (three numbers), or null. */
export function rawTriple(v: Json | undefined): Triple | null {
  if (!Array.isArray(v) || v.length !== 3) return null;
  const [a, b, c] = v as readonly Json[];
  if (typeof a !== 'number' || typeof b !== 'number' || typeof c !== 'number') return null;
  return [a, b, c];
}

/** End times of a raw JSON event list (entries without a valid endTime are skipped). */
export function rawEndTimes(list: readonly Json[]): Triple[] {
  const out: Triple[] = [];
  for (const ev of list) {
    if (typeof ev !== 'object' || ev === null || Array.isArray(ev)) continue;
    const t = rawTriple((ev as { readonly [k: string]: Json })['endTime']);
    if (t !== null) out.push(t);
  }
  return out;
}

function maxOf(a: number, b: number): number {
  // Rust f64::max: a NaN operand is ignored.
  if (Number.isNaN(b)) return a;
  if (Number.isNaN(a)) return b;
  return a > b ? a : b;
}

function listMax(acc: number, list: IrEventList<unknown> | undefined, sec: (t: Triple) => Sec): number {
  if (list === undefined) return acc;
  let m = acc;
  for (const e of list) m = maxOf(m, sec(e.endTime));
  return m;
}

/** The line's contribution to max_time in seconds (-Infinity when nothing on the line counts). */
export function lineMaxTime(line: IrLine, sec: (t: Triple) => Sec): number {
  let m = Number.NEGATIVE_INFINITY;
  for (const n of line.notes) m = maxOf(m, sec(n.endTime));
  for (const layer of line.eventLayers) {
    if (layer === null) continue;
    m = listMax(m, layer.alphaEvents, sec);
    m = listMax(m, layer.moveXEvents, sec);
    m = listMax(m, layer.moveYEvents, sec);
    m = listMax(m, layer.rotateEvents, sec);
  }
  const ext = extendedOf(line);
  if (ext !== null) {
    m = listMax(m, ext.scaleXEvents, sec);
    m = listMax(m, ext.scaleYEvents, sec);
    m = listMax(m, ext.textEvents, sec);
    const ignoredText = ignoredList(ext, 'textEvents');
    if (ignoredText !== null) for (const t of rawEndTimes(ignoredText)) m = maxOf(m, sec(t));
  }
  return m;
}

/** max(0, contributions...) + 1 s. */
export function combineMaxTime(contributions: Iterable<number>): Sec {
  let m = 0;
  for (const c of contributions) m = maxOf(m, c);
  return m + MAX_TIME_TAIL_SEC;
}

/** Phira's max_time (seconds, the +1 s included) of a lowered chart. */
export function computeMaxTime(lines: readonly IrLine[], sec: (t: Triple) => Sec): Sec {
  return combineMaxTime(lines.map((l) => lineMaxTime(l, sec)));
}
